import { useHistory } from 'react-router-dom';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import { useContext, useEffect, useState } from 'react';
import { AuthContext } from '../../contexts/AuthContext';

import DefaultCard from '../../components/default/DefaultCard';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import PageHeader from '../../components/PageHeader/PageHeader';
import '../../styles/page-components.css';

import { ServiceResponse } from '../../app/services/shared/interfaces';
import useFAQService from '../../app/services/hooks/useFAQService';
import DefaultModal from '../../components/default/DefaultModal';
import FAQCreate from './components/FAQCreate';
import FAQEdit from './components/FAQEdit';
import { AppContext } from '../../contexts/AppContext';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import FAQDataTable from './components/tables/FAQDataTable';
import { toast } from 'react-toastify';
import ButtonGeneral from '../../components/buttons/ButtonGeneral';

interface FAQ {
    id: number;
    question: string;
    answer: string;
    order: number;
    is_active: boolean;
    created_at: string;
}

const breadcrumbs = [
    { label: 'Inicio', path: '/intranet' },
    { label: 'Preguntas Frecuentes', isCurrent: true }
];

const FAQ = () => {
    const { showLoading, hideLoading, changeAnimation } = useContext(AppContext);
    const history = useHistory();
    const { fetchingGetFAQs, getFAQs, activeFAQ, deleteFAQ } = useFAQService();

    const { auth } = useContext(AuthContext);

    const [faqIdEdit, setFaqIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [faqs, setFaqs] = useState<FAQ[]>([]);
    const [totalRows, setTotalRows] = useState(0);

    const [showingCreate, setShowingCreate] = useState(false);

    const [filter, setFilter] = useState({
        page: 1,
        per_page: 10,
        sort: 'order',
        order: 'asc',
        search: '',
        is_active: ''
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order, filter.search, filter.is_active]);

    const reloadTable = () => {
        const filters: any = {};
        if (filter.search) filters.search = filter.search;
        if (filter.is_active !== '') filters.is_active = filter.is_active;
        
        getFAQs(filters, {
            onSuccess: (response: ServiceResponse) => {
                setFaqs(response.data.faqs || []);
                setTotalRows(response.data.total || 0);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
            }
        });
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const showEdit = (faqId: number) => {
        setShowingEdit(true);
        setFaqIdEdit(faqId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setFaqIdEdit(-1);
    };

    const onError = () => {
        setShowingEdit(false);
        setFaqIdEdit(-1);
    };

    const active = (faqId: number, active: boolean) => {
        if (showLoading) showLoading('loading', active ? 'Activando...' : 'Desactivando...');
        activeFAQ(faqId, active, {
            onSuccess: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().successAlert({
                    title: 'Éxito',
                    message: response.message
                });

                reloadTable();
            },
            onError: (response: ServiceResponse) => {
                if (hideLoading) hideLoading();

                useReactConfirmAlert().errorAlert({
                    title: 'Error',
                    message: response.message
                });
            }
        });
    };

    const destroy = (faqId: number) => {
        const faq = faqs.find(f => f.id === faqId);
        const _text = `Está a punto de eliminar la pregunta: "${faq?.question.substring(0, 50)}..."`;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando pregunta frecuente...');
                        deleteFAQ(faqId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });
                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'La pregunta frecuente no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <div className="container-fluid">
                <div className="page-container">
                    {/* Header con breadcrumbs */}
                    <PageHeader
                        title=""
                        breadcrumbs={breadcrumbs}
                    />

                    {/* Header con título y botón de crear alineados */}
                    <DefaultCard>
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <div>
                                <h1 className="page-title">Gestión de Preguntas Frecuentes</h1>
                                <p className="page-description">
                                    Administra las preguntas frecuentes que se muestran en la landing page.{" "}
                                    <a href="#" className="description-link">
                                        Conoce más sobre la gestión de FAQs aquí
                                    </a>
                                </p>
                            </div>
                            <ButtonGeneral
                                onClick={showCreate}
                                title="Nueva pregunta frecuente"
                                variant="primary"
                                size="small"
                                icon="fa-plus"
                                iconPosition="left"
                            />
                        </div>

                        {/* Data Table */}
                        <FAQDataTable
                            active={active}
                            faqs={faqs}
                            totalRows={totalRows}
                            loading={fetchingGetFAQs}
                            edit={showEdit}
                            destroy={destroy}
                            filter={filter}
                            setFilter={setFilter}
                            callbackSuccess={reloadTable}
                            onSearch={reloadTable}
                        />
                    </DefaultCard>
                </div>
            </div>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear pregunta frecuente"
                    backdrop={true}
                    showFooter={false}
                >
                    <FAQCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Pregunta Frecuente"
                    backdrop={true}
                    showFooter={false}
                >
                    <FAQEdit
                        faqId={faqIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={onError}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default FAQ;

